import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { auth, db } from '../firebase/config';
import { doc, getDoc, getDocFromServer } from 'firebase/firestore';
import { subscriptionService } from '../services/subscriptionService';
import { usageTrackingService } from '../services/usageTracking';
import { checkBackendHealth } from '../services/healthService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      // Keep loading true until everything is fetched
      setLoading(true);
      setUser(user);

      if (user) {
        // Initialize usage tracking with user
        usageTrackingService.initializeUser(user);

        try {
          // Small delay to ensure Firestore is ready after auth
          await new Promise(resolve => setTimeout(resolve, 100));

          // Health gate: verify the backend can authenticate before proceeding
          const idToken = await user.getIdToken(true); // force refresh to ensure proper ID token

          // Client-side diagnostic: decode JWT header to check for 'kid' claim
          try {
            const parts = idToken.split('.');
            const header = JSON.parse(atob(parts[0]));
            console.log('[AuthContext] ID token header:', JSON.stringify(header));
            console.log('[AuthContext] Token has kid?', !!header.kid, '| kid:', header.kid);
          } catch (e) {
            console.warn('[AuthContext] Could not decode token header:', e.message);
          }

          const health = await checkBackendHealth(idToken);
          if (!health.healthy) {
            throw new Error(
              `Backend services unavailable: ${health.error || 'unknown error'}. ` +
              'Please try again later or contact support if this persists.'
            );
          }

          let profile = await loadUserProfile(user.uid);

          // Self-healing: if user is authenticated but has no Firestore doc
          // (e.g. profile creation failed during signup), create it now
          if (!profile) {
            console.warn('[AuthContext] No user profile found — attempting server-side creation');
            try {
              const createResp = await fetch('/api/create-user-profile', {
                method: 'POST',
                headers: {
                  'X-Firebase-Token': idToken,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({ displayName: user.displayName || '' }),
              });

              if (createResp.ok) {
                console.log('[AuthContext] User profile created via self-healing');
                profile = await loadUserProfile(user.uid);
              } else {
                console.error('[AuthContext] Self-healing profile creation failed:', createResp.status);
              }
            } catch (healErr) {
              console.error('[AuthContext] Self-healing error:', healErr.message);
            }
          }

          setUserProfile(profile);

          // Check if user is returning from checkout (URL param)
          const urlParams = new URLSearchParams(window.location.search);
          const fromCheckout = urlParams.get('success') === 'true';

          // Get subscription - will auto-verify if status is problematic
          const sub = await subscriptionService.getUserSubscription(user.uid, {
            verify: fromCheckout, // Force verify if coming from checkout
            verifyReason: fromCheckout ? 'checkout' : 'login',
          });

          setSubscription(sub);

          // Clear checkout success param from URL
          if (fromCheckout) {
            urlParams.delete('success');
            const newUrl = urlParams.toString()
              ? `${window.location.pathname}?${urlParams}`
              : window.location.pathname;
            window.history.replaceState({}, '', newUrl);
          }
        } catch (error) {
          console.error('Error loading user data:', error);
          // Set a fallback subscription state so the app doesn't get stuck on "Loading subscription..."
          // This allows the user to access billing/plans pages to fix their subscription
          setSubscription({
            status: 'error',
            tier: null,
            usageCount: 0,
            usageLimit: null,
            isActive: false,
            canUseService: false,
            error: error.message,
          });
        }
      } else {
        usageTrackingService.clearUser();
        setUserProfile(null);
        setSubscription(null);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const loadUserProfile = async (uid) => {
    try {
      const userRef = doc(db, 'users', uid);

      // Read from the server, mirroring subscriptionService. usageTracking
      // fires an un-awaited merge write on this document at login, and a
      // cache-first read can resolve to just that pending write's fields,
      // dropping server-only ones like isEmployee.
      let userSnap;
      try {
        userSnap = await getDocFromServer(userRef);
      } catch (e) {
        console.warn('Server fetch failed for user profile, using cache:', e.message);
        userSnap = await getDoc(userRef);
      }

      if (userSnap.exists()) {
        return userSnap.data();
      }
      return null;
    } catch (error) {
      console.error('Error loading user profile:', error);
      return null;
    }
  };

  const signup = async (email, password, displayName = '') => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Create user profile server-side (Admin SDK bypasses Firestore security rules
      // which block client-side document creation to protect billing fields)
      const idToken = await user.getIdToken();
      const response = await fetch('/api/create-user-profile', {
        method: 'POST',
        headers: {
          'X-Firebase-Token': idToken,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ displayName: displayName || '' }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `Profile creation failed (${response.status})`);
      }

      return { success: true, user };
    } catch (error) {
      let errorMessage = error.message;
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'This email is already registered';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password should be at least 6 characters';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address';
      }
      return { success: false, error: errorMessage };
    }
  };

  const login = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      return { success: true, user: userCredential.user };
    } catch (error) {
      let errorMessage = error.message;
      if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
        errorMessage = 'Invalid email or password';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many failed attempts. Please try again later';
      }
      return { success: false, error: errorMessage };
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      return { success: true };
    } catch (error) {
      if (error.code === 'auth/user-not-found') {
        return { success: true };
      } else if (error.code === 'auth/too-many-requests') {
        return { success: false, error: 'Too many requests. Please try again later' };
      } else if (error.code === 'auth/invalid-email') {
        return { success: false, error: 'Invalid email address' };
      }
      return { success: false, error: 'Unable to send reset email. Please try again' };
    }
  };

  /**
   * Refresh subscription - verifies with Paddle API
   * @param {string} reason - 'manual' (user clicked refresh) or 'checkout' (after purchase)
   */
  const refreshSubscription = async (reason = 'manual') => {
    if (!user) return;
    try {
      // Force verify with Paddle
      const sub = await subscriptionService.getUserSubscription(user.uid, {
        verify: true,
        verifyReason: reason,
      });
      setSubscription(sub);

      const profile = await loadUserProfile(user.uid);
      setUserProfile(profile);

      return sub;
    } catch (error) {
      console.error('Error refreshing subscription:', error);
      throw error;
    }
  };

  /**
   * Reload subscription from Firestore only (no Paddle verification).
   * Use after actions that change Firestore data (e.g. video processing count)
   * where we just need to re-read the latest values without triggering reconciliation.
   */
  const reloadSubscription = async () => {
    if (!user) return;
    try {
      const sub = await subscriptionService.getUserSubscription(user.uid, {
        verify: false,
      });
      setSubscription(sub);
      return sub;
    } catch (error) {
      console.error('Error reloading subscription:', error);
      throw error;
    }
  };

  const value = {
    user,
    userProfile,
    // Gates the internal Azure storage browser. Only drives what the UI offers;
    // the storage gateway re-reads the flag from Firestore on every request, so
    // faking it client-side just earns a 403.
    isEmployee: !!userProfile?.isEmployee,
    subscription,
    loading,
    signup,
    login,
    logout,
    resetPassword,
    refreshSubscription,
    reloadSubscription,
  };

  return <AuthContext.Provider value={value}>{!loading && children}</AuthContext.Provider>;
};
